import { useState } from 'react';
import type { AuditLogRow } from '@app/shared';
import { Modal } from '../components/Modal';
import { useAuditLogs } from '../features/audit-log/api';

export function AuditLogPage(): JSX.Element {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [page, setPage] = useState(1);
  const [detailRow, setDetailRow] = useState<AuditLogRow | null>(null);
  const pageSize = 50;
  const { data, isLoading, isError, error } = useAuditLogs({
    page,
    pageSize,
    ...(action ? { action } : {}),
    ...(entityType ? { entityType } : {}),
  });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold">稽核日誌</h1>
      </header>

      <div className="flex flex-wrap gap-3 mb-4 bg-white p-3 rounded border">
        <label className="grid text-sm">
          <span className="text-slate-600">Action</span>
          <input
            type="text"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            placeholder="e.g. USER.CREATED"
            className={inputCls}
          />
        </label>
        <label className="grid text-sm">
          <span className="text-slate-600">Entity</span>
          <input
            type="text"
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setPage(1);
            }}
            placeholder="e.g. customer"
            className={inputCls}
          />
        </label>
      </div>

      {isLoading ? (
        <div role="status" className="text-slate-500">
          載入中...
        </div>
      ) : isError ? (
        <div role="alert" className="text-red-600">
          載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
        </div>
      ) : (
        <>
          <table className="w-full bg-white shadow-sm rounded-md">
            <thead>
              <tr className="border-b text-left text-sm text-slate-600">
                <th className="px-4 py-2">時間</th>
                <th className="px-4 py-2">使用者</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">Entity</th>
                <th className="px-4 py-2">IP</th>
                <th className="px-4 py-2 w-20 text-right">動作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50 text-sm">
                  <td className="px-4 py-2 whitespace-nowrap">
                    {new Date(r.createdAt).toLocaleString('zh-TW')}
                  </td>
                  <td className="px-4 py-2">
                    {r.user?.displayName ?? <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2 font-mono">{r.action}</td>
                  <td className="px-4 py-2">{r.entityType}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.ip ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setDetailRow(r)}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      詳細
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-4 text-center text-slate-500">
                    無資料
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>

          <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
            <span>共 {total} 筆</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="border px-3 py-1 rounded disabled:opacity-50"
              >
                上一頁
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="border px-3 py-1 rounded disabled:opacity-50"
              >
                下一頁
              </button>
            </div>
          </div>
        </>
      )}

      <Modal
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        title="稽核日誌詳細"
        widthClass="max-w-3xl"
      >
        {detailRow ? <AuditLogDetail row={detailRow} /> : null}
      </Modal>
    </section>
  );
}

function AuditLogDetail({ row }: { row: AuditLogRow }): JSX.Element {
  return (
    <dl className="grid grid-cols-[7rem_1fr] gap-y-2 gap-x-4 text-sm">
      <dt className="text-slate-500">時間</dt>
      <dd>{new Date(row.createdAt).toLocaleString('zh-TW')}</dd>

      <dt className="text-slate-500">使用者</dt>
      <dd>
        {row.user ? (
          <>
            {row.user.displayName}{' '}
            <span className="text-slate-400">({row.user.username})</span>
          </>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </dd>

      <dt className="text-slate-500">Action</dt>
      <dd className="font-mono">{row.action}</dd>

      <dt className="text-slate-500">Entity</dt>
      <dd>
        {row.entityType} <span className="text-slate-400">#{row.entityId}</span>
      </dd>

      <dt className="text-slate-500">IP</dt>
      <dd className="font-mono text-xs">{row.ip ?? '—'}</dd>

      <dt className="text-slate-500">User Agent</dt>
      <dd className="font-mono text-xs break-all">{row.userAgent ?? '—'}</dd>

      <dt className="text-slate-500 self-start pt-1">Diff</dt>
      <dd>
        {row.diff ? (
          <pre className="bg-slate-50 border rounded p-3 text-xs overflow-x-auto max-h-96">
            {JSON.stringify(row.diff, null, 2)}
          </pre>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </dd>
    </dl>
  );
}

const inputCls =
  'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm w-48';
